import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';

// Google Auth Provider - only create if Firebase is configured
let googleProvider: GoogleAuthProvider | null = null;
if (isFirebaseConfigured) {
  googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account',
  });
}

/**
 * Sign in with Google popup
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
  if (!auth || !googleProvider) {
    throw new Error('Firebase is not configured');
  }
  return signInWithPopup(auth, googleProvider);
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  if (!auth) {
    throw new Error('Firebase is not configured');
  }
  return signInWithEmailAndPassword(auth, email, password);
};

/**
 * Create a new user with email and password
 */
export const signUpWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  if (!auth) {
    throw new Error('Firebase is not configured');
  }
  return createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Sign out the current user
 */
export const logout = async (): Promise<void> => {
  if (!auth) return;
  return signOut(auth);
};

/**
 * Subscribe to auth state changes
 */
export const onAuthChange = (
  callback: (user: User | null) => void
): (() => void) => {
  if (!auth) {
    // Return a no-op unsubscribe function
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

/**
 * Get the current user's ID token
 */
export const getIdToken = async (): Promise<string | null> => {
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};

/**
 * Get the current user's ID token with claims
 */
export const getIdTokenResult = async () => {
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdTokenResult();
};

/**
 * Get the current authenticated user
 */
export const getCurrentUser = (): User | null => {
  if (!auth) return null;
  return auth.currentUser;
};

/**
 * Force refresh the current user's token
 */
export const refreshToken = async (): Promise<string | null> => {
  if (!auth) return null;
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(true);
};
